import { useEffect, useRef, useState } from 'react'
import { useEntryMutations, useSummary } from '../hooks/useApi'
import {
    formatClock,
    formatDurationCompact,
    formatMoney,
    formatTime,
    fromLocalInputValue,
    startOfDayISO,
    startOfWeekISO,
} from '../lib/format'
import { errText } from '../lib/errors'
import { useT } from '../i18n/context'
import { useTimerStore } from '../stores/timer'
import { useActiveWorkspaceId } from '../stores/workspace'
import { toast } from '../stores/toast'
import type { TimeEntryType } from '../types/api'
import { Button } from '../components/ui/Button'
import { Card, CardBody, CardHeader } from '../components/ui/Card'
import { Field, Input, Select } from '../components/ui/Field'
import { DateTimeInput } from '../components/ui/DateTimeInputs'
import {
    ABSENCE_TYPES,
    TRACK_TYPES,
    entryTypeMeta,
    type TrackType,
} from '../lib/labelMaps'
import { ProjectSelect } from '../components/ProjectSelect'
import { MonthEntriesCard } from '../components/MonthEntriesCard'

const typeButtonTones: Record<
    TrackType,
    { active: string; idle: string; dot: string }
> = {
    WORK: {
        active: 'bg-indigo-700 dark:bg-indigo-600 text-white hover:bg-indigo-800 dark:hover:bg-indigo-500 focus-visible:outline-indigo-700',
        idle: 'text-slate-600 dark:text-slate-300 hover:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 hover:text-indigo-700 dark:hover:text-indigo-300 focus-visible:outline-indigo-600',
        dot: 'bg-indigo-500',
    },
    BREAK: {
        active: 'bg-amber-500 text-white hover:bg-amber-600 focus-visible:outline-amber-500',
        idle: 'text-slate-600 dark:text-slate-300 hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/60 hover:text-amber-700 focus-visible:outline-amber-500',
        dot: 'bg-amber-400',
    },
    MEETING: {
        active: 'bg-violet-600 text-white hover:bg-violet-700 focus-visible:outline-violet-600',
        idle: 'text-slate-600 dark:text-slate-300 hover:border-violet-500 hover:bg-violet-50 dark:hover:bg-violet-950/60 hover:text-violet-700 focus-visible:outline-violet-500',
        dot: 'bg-violet-500',
    },
    ADMIN: {
        active: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline-blue-600',
        idle: 'text-slate-600 dark:text-slate-300 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/60 hover:text-blue-700 focus-visible:outline-blue-500',
        dot: 'bg-blue-500',
    },
}

export function TrackPage() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const running = useTimerStore(s => s.running)
    const elapsed = useTimerStore(s => s.elapsed)
    const syncing = useTimerStore(s => s.syncing)
    const draft = useTimerStore(s => s.draft)
    const setDraft = useTimerStore(s => s.setDraft)
    const toggleTimer = useTimerStore(s => s.toggle)
    const updateRunning = useTimerStore(s => s.updateRunning)
    const [manualOpen, setManualOpen] = useState(false)

    const today = useSummary(workspaceId, { startDate: startOfDayISO() })
    const week = useSummary(workspaceId, { startDate: startOfWeekISO() })

    // Pending Beschreibungs-PATCH beim Unmount verwerfen
    const patchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(
        () => () => {
            if (patchTimer.current) clearTimeout(patchTimer.current)
        },
        [],
    )

    const handleTypeClick = async (entryType: TrackType) => {
        try {
            const result = await toggleTimer(workspaceId!, entryType)
            if (result === 'started')
                toast.success(
                    t('timer.started', {
                        type: t(entryTypeMeta[entryType].labelKey),
                    }),
                )
            else if (result === 'stopped') toast.success(t('timer.stopped'))
            else
                toast.success(
                    t('timer.switched', {
                        type: t(entryTypeMeta[entryType].labelKey),
                    }),
                )
        } catch (err) {
            toast.error(errText(err))
        }
    }

    const handleDescriptionChange = (value: string) => {
        setDraft({ description: value })
        if (patchTimer.current) clearTimeout(patchTimer.current)
        patchTimer.current = setTimeout(() => {
            patchTimer.current = null
            // Store frisch lesen — der laufende Eintrag kann inzwischen gewechselt sein
            const entry = useTimerStore.getState().running
            if (!entry || entry.description === value) return
            updateRunning({ description: value }).catch(err =>
                toast.error(errText(err)),
            )
        }, 700)
    }

    const handleProjectChange = (
        projectId: number | null,
        clientId: number | null,
    ) => {
        setDraft({ projectId, clientId })
        if (running)
            updateRunning({ projectId, clientId }).catch(err =>
                toast.error(errText(err)),
            )
    }

    return (
        <div className='space-y-6'>
            {/* Timer */}
            <Card>
                <CardBody className='space-y-4 py-6'>
                    <div className='flex flex-col items-center gap-4 md:flex-row md:items-center md:justify-between md:gap-8'>
                        <div className='shrink-0 text-center md:text-left'>
                            <div
                                className={`font-mono text-5xl font-bold tabular-nums tracking-tight ${
                                    running ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-100'
                                }`}
                            >
                                {formatClock(running ? elapsed : 0)}
                            </div>
                            <p className='mt-1 text-xs text-slate-500 dark:text-slate-400'>
                                {running
                                    ? t('timer.runningSince', {
                                          type: t(
                                              entryTypeMeta[running.entryType]
                                                  .labelKey,
                                          ),
                                          time: formatTime(running.startTime),
                                      })
                                    : t('timer.ready')}
                            </p>
                        </div>
                        <div className='grid w-full grid-cols-2 justify-items-center gap-2 md:flex md:justify-end'>
                            {TRACK_TYPES.map(type => {
                                const isActive = running?.entryType === type
                                const tone = typeButtonTones[type]
                                const typeLabel = t(entryTypeMeta[type].labelKey)
                                return (
                                    <button
                                        key={type}
                                        type='button'
                                        disabled={syncing}
                                        aria-pressed={isActive}
                                        title={
                                            isActive
                                                ? t('timer.stopType', {
                                                      type: typeLabel,
                                                  })
                                                : running
                                                  ? t('timer.switchTo', {
                                                        type: typeLabel,
                                                    })
                                                  : t('timer.startType', {
                                                        type: typeLabel,
                                                    })
                                        }
                                        onClick={() => void handleTypeClick(type)}
                                        className={`flex h-[4.5rem] w-[4.5rem] flex-col items-center justify-center gap-1.5 rounded-xl border transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                                            isActive
                                                ? `border-transparent ${tone.active}`
                                                : `border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 ${tone.idle}`
                                        }`}
                                    >
                                        <span
                                            className={`h-1.5 w-1.5 rounded-full ${
                                                isActive
                                                    ? 'bg-white dark:bg-slate-900'
                                                    : tone.dot
                                            }`}
                                            aria-hidden='true'
                                        />
                                        <span className='text-sm leading-none font-medium'>
                                            {typeLabel}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                    <div className='flex flex-col gap-2 sm:flex-row'>
                        <Input
                            className='flex-1'
                            placeholder={t('track.descriptionPlaceholder')}
                            value={draft.description}
                            onChange={e =>
                                handleDescriptionChange(e.target.value)
                            }
                        />
                        <div className='w-full shrink-0 sm:w-72'>
                            <ProjectSelect
                                value={draft}
                                onChange={handleProjectChange}
                            />
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Summary */}
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <SummaryCard
                    title={t('track.today')}
                    summary={today.data}
                    loading={today.isLoading}
                />
                <SummaryCard
                    title={t('track.thisWeek')}
                    summary={week.data}
                    loading={week.isLoading}
                />
            </div>

            {/* Manuelle Erfassung */}
            <Card>
                <CardHeader
                    title={t('track.manualTitle')}
                    actions={
                        <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => setManualOpen(o => !o)}
                        >
                            {manualOpen
                                ? t('track.hide')
                                : t('track.add')}
                        </Button>
                    }
                />
                {manualOpen && (
                    <CardBody>
                        <ManualEntryForm
                            workspaceId={workspaceId!}
                            onDone={() => setManualOpen(false)}
                        />
                    </CardBody>
                )}
            </Card>

            {/* Einträge des gewählten Monats (Default: aktueller Monat) */}
            <MonthEntriesCard />
        </div>
    )
}

function SummaryCard({
    title,
    summary,
    loading,
}: {
    title: string
    summary:
        | {
              totalSeconds: number
              billableSeconds: number
              earnings: number
              entryCount: number
          }
        | undefined
    loading: boolean
}) {
    const t = useT()
    return (
        <Card>
            <CardBody>
                <p className='text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                    {title}
                </p>
                {loading || !summary ? (
                    <div className='mt-2 h-9 animate-pulse rounded bg-slate-100 dark:bg-slate-800' />
                ) : (
                    <>
                        <p className='mt-1 text-3xl font-bold text-slate-800 dark:text-slate-100'>
                            {formatDurationCompact(summary.totalSeconds)}
                        </p>
                        <p className='mt-1 text-sm text-slate-500 dark:text-slate-400'>
                            {t('track.summaryLine', {
                                billable: formatDurationCompact(
                                    summary.billableSeconds,
                                ),
                                money: formatMoney(summary.earnings),
                                count: summary.entryCount,
                            })}
                        </p>
                    </>
                )}
            </CardBody>
        </Card>
    )
}

function ManualEntryForm({
    workspaceId,
    onDone,
}: {
    workspaceId: number
    onDone: () => void
}) {
    const t = useT()
    const { create } = useEntryMutations(workspaceId)
    const [description, setDescription] = useState('')
    const [projectId, setProjectId] = useState<number | null>(null)
    const [clientId, setClientId] = useState<number | null>(null)
    const [entryType, setEntryType] = useState<TimeEntryType>('WORK')
    const [isBillable, setIsBillable] = useState(true)
    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!start || !end) return
        if (new Date(end) < new Date(start)) {
            toast.error(t('track.endBeforeStart'))
            return
        }
        // Abwesenheiten sind serverseitig nie fakturierbar — Checkbox
        // wird dafür gar nicht erst angeboten (KONZEPT 4.4)
        const absence = ABSENCE_TYPES.has(entryType)
        create.mutate(
            {
                description,
                projectId: projectId ?? undefined,
                clientId: clientId ?? undefined,
                entryType,
                isBillable: absence ? false : isBillable,
                startTime: fromLocalInputValue(start),
                endTime: fromLocalInputValue(end),
            },
            {
                onSuccess: () => {
                    toast.success(t('track.entrySaved'))
                    onDone()
                },
                onError: err => toast.error(errText(err)),
            },
        )
    }

    return (
        <form onSubmit={submit} className='grid grid-cols-1 gap-3 md:grid-cols-2'>
            <Field label={t('track.description')} className='md:col-span-2'>
                <Input
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder={t('track.manualDescriptionPlaceholder')}
                />
            </Field>
            <Field label={t('track.projectClient')}>
                <ProjectSelect
                    value={{ projectId, clientId }}
                    onChange={(p, c) => {
                        setProjectId(p)
                        setClientId(c)
                    }}
                />
            </Field>
            <Field label={t('track.type')}>
                <Select
                    value={entryType}
                    onChange={e => setEntryType(e.target.value as TimeEntryType)}
                >
                    {Object.entries(entryTypeMeta).map(([value, meta]) => (
                        <option key={value} value={value}>
                            {t(meta.labelKey)}
                        </option>
                    ))}
                </Select>
            </Field>
            <Field label={t('track.start')}>
                <DateTimeInput value={start} onChange={setStart} />
            </Field>
            <Field label={t('track.end')}>
                <DateTimeInput value={end} onChange={setEnd} />
            </Field>
            {!ABSENCE_TYPES.has(entryType) && (
                <label className='flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300 md:col-span-2'>
                    <input
                        type='checkbox'
                        checked={isBillable}
                        onChange={e => setIsBillable(e.target.checked)}
                        className='h-4 w-4 cursor-pointer rounded border-slate-300 dark:border-slate-600 accent-indigo-700 dark:accent-indigo-500'
                    />
                    {t('track.billable')}
                </label>
            )}
            <div className='md:col-span-2'>
                <Button type='submit' loading={create.isPending}>
                    {t('track.saveEntry')}
                </Button>
            </div>
        </form>
    )
}
